import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, Globe2, Store, ShoppingBag, LayoutGrid, Monitor,
  Warehouse, Users, ArrowUpRight,
} from "lucide-react";
import { useStore } from "../../store";
import { ThemeStrip } from "../../design-system/components/ThemePicker";
import { apiMode, services } from "@/services";

const DEMO_EMAIL: Record<string, string> = {
  seller_1: "sell@techzone.in",
  seller_2: "sell@homestyle.in",
  warehouse: "kavitha@demo-commerce.store",
  merchant_owner: "admin@demo-commerce.store",
  platform_admin: "super@mce.dev",
  customer: "aarav.sharma@example.com",
  b2b_buyer: "ananya@infosys-demo.test",
  cashier_online: "preethi@demo-commerce.store",
  cashier_offline: "preethi@demo-commerce.store",
};

const APPS = [
  {
    id: "world",
    title: "World view",
    desc: "Every app as a live 3D city",
    path: "/admin/world",
    scenarioId: "merchant_owner",
    icon: Globe2,
  },
  {
    id: "merchant",
    title: "Merchant",
    desc: "Catalog, orders, wallet & marketing",
    path: "/admin",
    scenarioId: "merchant_owner",
    icon: Store,
  },
  {
    id: "platform",
    title: "Platform",
    desc: "Tenants, plans & billing",
    path: "/super-admin",
    scenarioId: "platform_admin",
    icon: Building2,
  },
  {
    id: "seller",
    title: "Seller",
    desc: "Offers, orders & payouts",
    path: "/seller",
    scenarioId: "seller_1",
    icon: ShoppingBag,
  },
  {
    id: "storefront",
    title: "Storefront",
    desc: "Shop, cart & checkout",
    path: "/",
    scenarioId: "customer",
    icon: LayoutGrid,
  },
  {
    id: "b2b",
    title: "B2B",
    desc: "RFQs, POs & credit",
    path: "/b2b",
    scenarioId: "b2b_buyer",
    icon: Users,
  },
  {
    id: "pos",
    title: "POS",
    desc: "Terminal, shifts & sync",
    path: "/pos",
    scenarioId: "cashier_online",
    icon: Monitor,
  },
  {
    id: "warehouse",
    title: "Warehouse",
    desc: "Pick, pack & transfers",
    path: "/warehouse",
    scenarioId: "warehouse",
    icon: Warehouse,
  },
] as const;

export function AppsPage() {
  const navigate = useNavigate();
  const setScenario = useStore((s) => s.setScenario);

  async function openApp(app: (typeof APPS)[number]) {
    setScenario(app.scenarioId);
    const email = DEMO_EMAIL[app.scenarioId];
    if (apiMode && email) {
      try { await services.auth.login({ email, password: "demo123" }); } catch { /* stay on demo token */ }
    }
    navigate(app.path);
  }

  const [params] = useSearchParams();
  const launched = useRef(false);
  useEffect(() => {
    const app = APPS.find((a) => a.id === params.get("open"));
    if (app && !launched.current) {
      launched.current = true;
      void openApp(app);
    }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="apps-root">
      <div className="apps-shell anim-fade-up">
        <header className="apps-hero">
          <div className="apps-brand">SC</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="apps-kicker">SYNCORB Commerce</div>
            <h1>Apps</h1>
            <p>Same brand, same theme — open any surface of the commerce platform.</p>
          </div>
        </header>

        <ThemeStrip className="apps-theme-strip" />

        <div className="apps-grid">
          {APPS.map((app) => {
            const Icon = app.icon;
            return (
              <button
                key={app.id}
                type="button"
                className="apps-card"
                onClick={() => openApp(app)}
              >
                <div className="apps-card-ic">
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <div className="apps-card-body">
                  <div className="apps-card-title">
                    {app.title}
                    <ArrowUpRight size={14} className="apps-card-arrow" />
                  </div>
                  <div className="apps-card-desc">{app.desc}</div>
                  <div className="apps-card-path mono">{app.path}</div>
                </div>
              </button>
            );
          })}
        </div>

        <footer className="apps-foot">
          Theme Brand follows tenant colours. Demo roles switch when you open an app.
        </footer>
      </div>
    </div>
  );
}
